import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Grid, Link } from '@mui/material';
import { Autocomplete, Button, TextField } from '@totalsoft/rocket-ui';
import { RightsContext } from 'providers/RightsContext';
import { Prompt } from 'react-router-dom';
import { compose } from 'redux';
import useDealerOptions from '../hooks/useDealerOptions';

export const OfferAssetGeneralDetailsComponentAlpha = ({
  t,
  generalData,
  onGeneralDetailsChange,
  responsibleOptions,
  sources,
  channels,
  partnerPriorities,
  offerTypes,
  onSave,
  savingGeneralData,
  isDirty,
  isFacilityFrame,
  processing,
  asmResponsibleOptions,
  workLocations,
  dealers,
  userInfo,
  validationInfo
}) => {
  const { readOnlyFull, offerPartnerPriorityWrite, assetGeneralDataWrite, isImage } = useContext(RightsContext);

  const accountManager = { responsibleId: generalData?.accountManagerId, name: generalData?.accountManager };

  const optionEqualsValueAutocomplete = useCallback((option, value) => option.responsibleId === value?.responsibleId, []);
  const { dealerOptions, workLocationOptions } = useDealerOptions({ dealers, workLocations, generalData, userInfo });

  return (
    <>
      <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
      <Grid container spacing={2}>
        {generalData.isOfferCopied && (
          <Grid item xs={12} md={12}>
            {t('OfferEdit.Asset.GeneralDetails.OriginalOffer')}
            <Link
              underline='always'
              onClick={() => {
                window.open(`/offers/${generalData.sourceDocumentId}/${generalData.sourceSiteId}`, '_blank');
              }}
            >
              {generalData.sourceDocumentNumber}
            </Link>
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <TextField
            id={'offerAssetGeneralCard_userName'}
            disabled
            fullWidth
            name='userName'
            value={generalData.userName || ''}
            label={t('OfferEdit.Asset.GeneralDetails.UserName')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            id={'offerAssetGeneralCard_responsible'}
            disabled={readOnlyFull || !assetGeneralDataWrite || processing}
            fullWidth
            value={generalData}
            valueKey='responsibleId'
            labelKey='responsibleName'
            label={t('OfferEdit.Asset.GeneralDetails.Responsible')}
            options={responsibleOptions}
            onChange={value => onGeneralDetailsChange({ responsibleId: value?.responsibleId, responsibleName: value?.responsibleName })}
            isClearable={false}
            simpleValue={false}
            isOptionEqualToValue={optionEqualsValueAutocomplete}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          {dealers && dealers.length > 0 ? (
            <Autocomplete
              id={'offerAssetGeneralCard_dealer'}
              disabled={true}
              name='responsiblePartner'
              label={t('OfferEdit.Asset.GeneralDetails.Dealer')}
              fullWidth
              required
              error={Boolean(validationInfo.responsiblePartnerId)}
              helperText={validationInfo.responsiblePartnerId}
              value={{ id: generalData.responsiblePartnerId, name: generalData.responsiblePartnerName }}
              onChange={e =>
                onGeneralDetailsChange({
                  responsiblePartnerId: e?.id,
                  responsiblePartnerName: e?.name,
                  workLocationId: null,
                  workLocationName: null
                })
              }
              options={dealerOptions}
              valueKey='id'
              labelKey='name'
              isClearable={true}
              isSearchable={true}
            />
          ) : (
            <TextField
              id={'offerAssetGeneralCard_dealer'}
              fullWidth
              disabled
              name='responsiblePartner'
              value={generalData.responsiblePartnerName || ''}
              label={t('OfferEdit.Asset.GeneralDetails.Dealer')}
            />
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {workLocations && workLocations.length > 0 ? (
            <Autocomplete
              id={'offerAssetGeneralCard_workLocation'}
              disabled={readOnlyFull || !assetGeneralDataWrite || processing || !generalData.responsiblePartnerId}
              name='workLocation'
              label={t('OfferEdit.Asset.GeneralDetails.WorkLocation')}
              fullWidth
              value={
                generalData.workLocationId && generalData.workLocationName
                  ? { id: generalData.workLocationId, name: generalData.workLocationName }
                  : null
              }
              onChange={e =>
                onGeneralDetailsChange({
                  workLocationId: e?.id,
                  workLocationName: e?.name
                })
              }
              options={workLocationOptions}
              valueKey='id'
              labelKey='name'
              isClearable={true}
              isSearchable={true}
            />
          ) : (
            <TextField
              id={'offerAssetGeneralCard_workLocation'}
              disabled
              fullWidth
              name='workLocation'
              value={generalData.workLocationName || ''}
              label={t('OfferEdit.Asset.GeneralDetails.WorkLocation')}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            id={'offerAssetGeneralCard_accountManager'}
            disabled={readOnlyFull || !assetGeneralDataWrite || processing}
            name='accountManager'
            label={t('OfferEdit.Asset.GeneralDetails.AccountManagerAlpha')}
            fullWidth
            value={accountManager}
            options={asmResponsibleOptions}
            valueKey='responsibleId'
            labelKey='name'
            onChange={e => onGeneralDetailsChange({ accountManagerId: e.responsibleId, accountManager: e.name })}
            isClearable={false}
          />
        </Grid>
        <Grid item xs={12} md={6} sx={{ display: 'none' }}>
          <Autocomplete
            id={'offerAssetGeneralCard_source'}
            disabled
            fullWidth
            value={generalData}
            valueKey='sourceTypeId'
            labelKey='sourceTypeName'
            label={t('OfferEdit.Asset.GeneralDetails.Source')}
            options={sources}
            onChange={onGeneralDetailsChange}
            isClearable={false}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            id={'offerAssetGeneralCard_salesChannel'}
            fullWidth
            name='channelType'
            value={generalData}
            valueKey='channelTypeId'
            labelKey='channelTypeName'
            label={t('OfferEdit.Asset.GeneralDetails.SalesChannel')}
            options={channels}
            onChange={e => onGeneralDetailsChange({ channelTypeId: e.channelTypeId, channelTypeName: e.channelTypeName })}
            disabled={readOnlyFull || !assetGeneralDataWrite || processing}
            isClearable={false}
          />
        </Grid>
        <Grid item xs={12} md={6} sx={{ display: 'none' }}>
          <Autocomplete
            id={'offerAssetGeneralCard_offerPriority'}
            disabled={!offerPartnerPriorityWrite || readOnlyFull || !assetGeneralDataWrite || processing}
            fullWidth
            value={generalData}
            valueKey='offerPriorityId'
            labelKey='offerPriorityName'
            label={t('OfferEdit.Asset.GeneralDetails.OfferPriority')}
            options={partnerPriorities}
            onChange={onGeneralDetailsChange}
            isClearable={false}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            id={'offerAssetGeneralCard_offerType'}
            fullWidth
            name='offerType'
            value={generalData}
            valueKey='offerTypeId'
            labelKey='offerTypeName'
            label={t('OfferEdit.Asset.GeneralDetails.OfferType')}
            options={offerTypes}
            onChange={e => onGeneralDetailsChange({ offerTypeId: e.offerTypeId, offerTypeName: e.offerTypeName })}
            disabled={readOnlyFull || !assetGeneralDataWrite || !isFacilityFrame || processing}
            isClearable={false}
          />
        </Grid>

        {!isImage && (
          <Grid item xs={12} mt={1}>
            <Button
              id={'offerAssetGeneralCard_saveBtn'}
              onClick={onSave}
              disabled={savingGeneralData || readOnlyFull || !isDirty || !assetGeneralDataWrite || processing}
            >
              {savingGeneralData ? t('Saving') + '...' : t('Save')}
            </Button>
          </Grid>
        )}
      </Grid>
    </>
  );
};

OfferAssetGeneralDetailsComponentAlpha.propTypes = {
  t: PropTypes.func.isRequired,
  generalData: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  onGeneralDetailsChange: PropTypes.func.isRequired,
  sources: PropTypes.array.isRequired,
  channels: PropTypes.array.isRequired,
  partnerPriorities: PropTypes.array.isRequired,
  offerTypes: PropTypes.array.isRequired,
  savingGeneralData: PropTypes.bool,
  isDirty: PropTypes.bool.isRequired,
  isFacilityFrame: PropTypes.bool,
  processing: PropTypes.bool,
  asmResponsibleOptions: PropTypes.array,
  responsibleOptions: PropTypes.array,
  workLocations: PropTypes.array.isRequired,
  dealers: PropTypes.array.isRequired,
  userInfo: PropTypes.object.isRequired,
  validationInfo: PropTypes.object
};

export default compose(withTranslation())(OfferAssetGeneralDetailsComponentAlpha);
